import React, { useRef, useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";

/**
 * Dropdown for image upload with two options: Upload photo (gallery) or Capture photo (camera).
 * Uses two hidden file inputs so iOS/Android open gallery vs camera correctly.
 */
const ImageSourceDropdown = ({
  onFileSelect,
  multiple = false,
  disabled = false,
  idPrefix,
  triggerLabel = "Add photo",
  triggerSubLabel,
  processing = false,
  triggerClassName,
}) => {
  const [open, setOpen] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const handleInputChange = (e) => {
    if (multiple) {
      const files = Array.from(e.target.files || []);
      if (files.length) onFileSelect(files);
    } else {
      const file = e.target.files?.[0];
      if (file) onFileSelect(file);
    }
    e.target.value = "";
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current?.contains(event.target) ||
        buttonRef.current?.contains(event.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const openGallery = () => {
    galleryInputRef.current?.click();
    setOpen(false);
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
    setOpen(false);
  };

  const baseTriggerClass =
    "flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg transition-all w-full ";
  const enabledClass =
    "border-border cursor-pointer hover:border-primary hover:bg-primary/5";
  const disabledClass = "border-border bg-muted/50 cursor-not-allowed";

  return (
    <>
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
        id={`${idPrefix}-gallery`}
        multiple={multiple}
        disabled={disabled}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleInputChange}
        className="hidden"
        id={`${idPrefix}-camera`}
        multiple={multiple}
        disabled={disabled}
      />
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && !processing && setOpen((o) => !o)}
          disabled={disabled || processing}
          className={
            triggerClassName ??
            baseTriggerClass +
              (disabled || processing ? disabledClass : enabledClass)
          }
        >
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            {processing ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
            ) : (
              <Icon name="Camera" size={20} className="text-primary" />
            )}
          </div>
          <p className="text-sm font-medium text-foreground">{triggerLabel}</p>
          {triggerSubLabel && (
            <p className="text-xs text-muted-foreground">{triggerSubLabel}</p>
          )}
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full z-[100] mt-1 py-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden"
          >
            <button
              type="button"
              onClick={openGallery}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <Icon name="Upload" size={18} className="text-primary" />
              Upload photo
            </button>
            <button
              type="button"
              onClick={openCamera}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <Icon name="Camera" size={18} className="text-primary" />
              Capture photo
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ImageSourceDropdown;
